import React, { useEffect, useState } from "react";
import BarekamInput from "../../components/input";
import { Recipient } from "../../shared/models/user/recipient/recipient";
import recipientService, {
  RecipientUpdateMeta,
} from "../../../services/recipient.service";
import Message from "../../components/message";
import { MessageSections } from "../../components/message/message";
import { AxiosError } from "axios";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { BarekamAttachment } from "../../components/attachment/attachment";
import { BarekamSelect } from "../../components/select/select";
import {
  BarekamMenuItem,
  SelectedBarekamMenuItem,
} from "../../components/select/styled/orders";
import { MessageLoader } from "../../../router/blocks/route-components/message.loader";
import moment from "moment";
import { Alert, DialogContent, Grid, PaperProps } from "@mui/material";
import { getCurrentUserData } from "../../../redux/slices/current-user.slice";
import { mapValidationErrors } from "../../user/settings/personal-info/personal-info.service";
import { StandardDialog, StandardDialogFooter } from "../../components/dialogs/standard-dialog/standard-dialog";
import BarekamDateInput from "../../components/input/barekam-date-input";
import BarekamPhoneInput from "../../auth/phone-input/phone-input";
import { getMessage } from "../../shared/helper-functions";
import allUsersService from "../../../services/all-users.service";
import { EditAddress } from "../../user/settings/user-addresses/edit-address/edit-address";
import { UserAddress } from "../../shared/models/user/user-address";
import BarekamButton, { SimpleButtons } from "../../components/button";
import BarekamText from "../../components/text";
import { getMyAddresses } from "../../../redux/slices/user-addresses.slice";
import { getProvinces, getCities, getRegions } from "../../../redux/slices/static-data.slice";

export const AddIdentityDocument = (props: AddIdentityDocumentProps) => {
  const { recipient } = props;
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const { recipientDocumentTypes } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const { provinces } = useAppSelector(
      (state) => state.staticDataReducer,
    );

  const [newRecipient, setNewRecipient] = useState<Recipient>({ ...recipient });
  const [fileInfo, setFileInfo] = useState<File>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [addressToEdit, setAddressToEdit] = useState<UserAddress | undefined>();
  const [isReady, setIsReady] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (newRecipient.document) {
      setFileInfo(newRecipient.document as File);
    }
  }, [newRecipient.document]);

  useEffect(() => {
    if (!isReady && !provinces.length) {
      Promise.allSettled([
        dispatch(getProvinces()),
        dispatch(getCities()),
        dispatch(getRegions()),
      ]).then(() => setIsReady(true));
    }
  }, [isReady])

  const handleChange = (
    value: any,
    fieldName:
      | "id"
      | "type"
      | "firstName"
      | "lastName"
      | "patronymic"
      | "phoneNumber"
      | "passportOrIdSerialNumber"
      | "documentExpirationDate"
      | "address"
      | "document"
      | "documentType"
      | "email",
  ) => {
    setErrors((prevState) => {
      delete prevState[fieldName];
      return prevState;
    });
    const modifiedRecipient = { ...newRecipient, [fieldName]: value };

    if (fieldName === "documentType") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      modifiedRecipient.documentType = recipientDocumentTypes.find(
        (documentType) => documentType.recipientDocumentTypeId === value,
      );
    }

    setNewRecipient(modifiedRecipient);
  };

  const handleFileSelect = async (file: File | undefined) => {
    setFileInfo(file);

    if (!file) {
      handleChange("", "document");
    }
  };

  function handleFileUpload() {
    const isNewFile = fileInfo instanceof File;

    if (isNewFile) {
      return fileInfo
        ?.stream()
        .getReader()
        .read()
        .then((fileData) => {
          if (fileData.value) {
            const fileName = fileInfo?.name || "Document";

            return new File([fileData.value], fileName, {
              type: fileInfo?.type,
            });
          } else {
            return null;
          }
        });
    }
    return Promise.resolve(null);
  }

  function passedValidations() {
    const errors = {} as { [key: string]: string };

    if (!newRecipient.documentType) {
      errors["documentType"] = "required";
    }
    if (!newRecipient.passportOrIdSerialNumber) {
      errors["passportOrIdSerialNumber"] = "required";
    }
    if (!newRecipient.documentExpirationDate) {
      errors["documentExpirationDate"] = "required";
    }
    if (!newRecipient.phoneNumber) {
      errors["phoneNumber"] = "required";
    }

    setErrors(errors);

    return !Object.keys(errors).length;
  }

  const editRecipient = async () => {
    if (passedValidations()) {
      const formData = new FormData();
      formData.append("recipientId", "" + newRecipient.recipientId);

      Object.keys(newRecipient).forEach(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (
          key:
            | "firstName"
            | "lastName"
            | "phoneNumber"
            | "passportOrIdSerialNumber"
            | "document"
            | "documentType",
        ) => {
          if (newRecipient[key] !== recipient[key]) {
            switch (key) {
              case "documentType":
                formData.append(
                  key,
                  newRecipient.documentType.recipientDocumentTypeId.toString(),
                );
                break;
              default:
                formData.append(key, "" + newRecipient[key]);
            }
          }
        },
      );

      try {
        handleFileUpload().then((fileInfo) => {
          if (fileInfo) {
            formData.append("file", fileInfo);
          }

          const modified = !!Array.from(formData.values()).length;

          if (modified) {
            return recipientService
              .editRecipient(recipient.recipientId, formData)
              .then((response: RecipientUpdateMeta) => {
                if (response.acknowledged && recipient.relatedUser.userId) {
                  allUsersService
                    .editUser(recipient.relatedUser.userId, {
                      phoneNumber: newRecipient.phoneNumber,
                    })
                    .then((response) => {
                      dispatch(getCurrentUserData());
                      props.recipientSaved(newRecipient);
                    });
                }
              })
              .catch((e) => {
                if (e instanceof AxiosError) {
                  setErrors(mapValidationErrors(e));
                }
              });
          }
        });
      } catch (e) {
        if (e instanceof AxiosError) {
          setErrors(mapValidationErrors(e));
        }
      }
    }
  };

  const handleEditAddressClose = (
    withChange: boolean,
    address?: UserAddress,
  ) => {
    if (!withChange) setAddressToEdit(undefined);

    if (withChange && address) {
      setNewRecipient((prevState) => {
        return {
          ...prevState,
          country: address.country,
          province: address.province,
          city: address.city,
          region: address.region,
          street: address.street,
          building: address.building,
          entrance: address.entrance,
          floor: address.floor,
          apartment: address.apartment,
          zipCode: address.zipCode,
        };
      });
      setAddressToEdit(undefined);
    }
  };

  return (
    <MessageLoader sectionName={MessageSections.USER}>
      <DialogContent>
        <Grid container columnSpacing={"1.5rem"} rowSpacing={"1.5rem"}>
          <Grid item xs={12}>
            <Alert severity="info" style={{ width: "100%" }}>
              <Message
                messageKey={"add_identity_document_info"}
                sectionId={MessageSections.USER}
              />
            </Alert>
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamSelect
              variant={"outlined"}
              defaultValue={""}
              label={getMessage(
                "document_type",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.documentType?.recipientDocumentTypeId}
              onChange={(event) =>
                handleChange(event.target.value, "documentType")
              }
              type={"standard"}
              required
              error={!!errors["documentType"]}
              errorMessage={
                errors["documentType"] &&
                getMessage(
                  errors["documentType"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            >
              {recipientDocumentTypes
                .filter((type) => type.name !== "tid")
                .map((type) => {
                  if (
                    type.recipientDocumentTypeId ===
                    newRecipient.documentType?.recipientDocumentTypeId
                  ) {
                    return (
                      <SelectedBarekamMenuItem
                        value={type.recipientDocumentTypeId}
                        key={type.name}
                      >
                        <Message
                          messageKey={type.name}
                          sectionId={MessageSections.USER}
                        />
                      </SelectedBarekamMenuItem>
                    );
                  } else {
                    return (
                      <BarekamMenuItem
                        value={type.recipientDocumentTypeId}
                        key={type.name}
                      >
                        <Message
                          messageKey={type.name}
                          sectionId={MessageSections.USER}
                        />
                      </BarekamMenuItem>
                    );
                  }
                })}
            </BarekamSelect>
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamPhoneInput
              key={"register-phone-number"}
              value={""}
              placeholder={getMessage(
                "phone_number",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              onChange={(value: number) => handleChange(value, "phoneNumber")}
              error={!!errors["phoneNumber"]}
              errorMessage={
                errors["phoneNumber"] &&
                getMessage(
                  errors["phoneNumber"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "passport_id",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.passportOrIdSerialNumber}
              onChange={(event) =>
                handleChange(event.target.value, "passportOrIdSerialNumber")
              }
              width={"100%"}
              error={!!errors["passportOrIdSerialNumber"]}
              errorMessage={
                errors["passportOrIdSerialNumber"] &&
                getMessage(
                  errors["passportOrIdSerialNumber"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamDateInput
              views={["year", "month", "day"]}
              value={
                newRecipient.documentExpirationDate
                  ? moment(newRecipient.documentExpirationDate)
                  : null
              }
              label={getMessage(
                "passport_expire_date",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              onChange={(event) =>
                handleChange(
                  event && event.format("YYYY-MM-DD"),
                  "documentExpirationDate",
                )
              }
              errorMessage={
                errors["documentExpirationDate"] &&
                getMessage(
                  errors["documentExpirationDate"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              disablePast
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamButton type={SimpleButtons.LINK} onClick={() => setAddressToEdit({} as UserAddress)}>
              <BarekamText variant={'xl'} bold>
                <Message messageKey={'add_address'} sectionId={MessageSections.ORDERS} />
              </BarekamText>
            </BarekamButton>
          </Grid>

          <StandardDialog headerLabel={<Message messageKey={addressToEdit?.addressId ? 'edit_address' : 'new_address'}
            sectionId={MessageSections.USER} />}
            open={!!addressToEdit}>
            <EditAddress address={addressToEdit || {} as UserAddress}
              onSave={handleEditAddressClose} isRecipientAddress />
          </StandardDialog>

          <Grid item xs={12}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"passport_image"}
                  sectionId={MessageSections.ORDERS}
                />
              }
              messageSection={MessageSections.USER}
              value={fileInfo}
              isSingle={true}
              isReadonly={false}
              onValueChange={(data: File) => handleFileSelect(data)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <StandardDialogFooter
        saveLabel={
          <Message messageKey={"save"} sectionId={MessageSections.USER} />
        }
        onSave={() => (addressToEdit || newRecipient?.building) ? editRecipient() : setAddressToEdit({} as UserAddress)}
        onCancel={() => props.popupClosed()}
      />
    </MessageLoader>
  );
};

interface AddIdentityDocumentProps extends PaperProps {
  recipient: Recipient;
  recipientSaved: (recipient: Recipient) => void;
  popupClosed: () => void;
}
